'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FullscreenImageViewer } from '@/components/ui/fullscreen-viewer';
import { onWorkspaceAdmissionEnd } from '@/providers/workspace-admission-lease';

type FullscreenAsset = {
  src: string;
  alt?: string;
  title?: string;
  type?: 'image' | 'video' | '3d';
};

type OpenOptions = {
  src: string;
  alt?: string;
  type?: 'image' | 'video' | '3d';
  assets?: Array<FullscreenAsset>;
  initialIndex?: number;
};

type FullscreenViewerContextValue = {
  isOpen: boolean;
  open: (options: OpenOptions) => void;
  close: () => void;
};

const FullscreenViewerContext =
  createContext<FullscreenViewerContextValue | null>(null);

export function FullscreenViewerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [assets, setAssets] = useState<Array<FullscreenAsset>>([]);
  const [initialIndex, setInitialIndex] = useState<number>(0);
  const [src, setSrc] = useState<string>('');
  const [alt, setAlt] = useState<string>('');
  const [type, setType] = useState<'image' | 'video' | '3d'>('image');

  const open = useCallback((options: OpenOptions) => {
    const { src, alt, type, assets: providedAssets, initialIndex } = options;
    const normalizedAssets: Array<FullscreenAsset> = Array.isArray(
      providedAssets
    )
      ? providedAssets
      : [{ src, alt, type }];

    const resolvedIndex =
      typeof initialIndex === 'number'
        ? initialIndex
        : Math.max(
            0,
            normalizedAssets.findIndex((a) => a.src === src)
          );

    setAssets(normalizedAssets);
    setInitialIndex(resolvedIndex);
    setSrc(src);
    setAlt(alt ?? 'Asset');
    setType(type ?? 'image');
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  // This provider sits above the admitted workspace subtree, so it keeps what
  // a workspace page opened in it (private signed URLs). When that admission
  // ends (workspace switch, membership loss, sign-out) close the viewer and
  // drop every URL in the same commit (#4628).
  useEffect(
    () =>
      onWorkspaceAdmissionEnd(() => {
        setIsOpen(false);
        setAssets([]);
        setSrc('');
        setAlt('');
        setInitialIndex(0);
      }),
    []
  );

  const value = useMemo<FullscreenViewerContextValue>(
    () => ({ isOpen, open, close }),
    [isOpen, open, close]
  );

  return (
    <FullscreenViewerContext.Provider value={value}>
      {children}
      <FullscreenImageViewer
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        src={src}
        alt={alt}
        type={type}
        assets={assets.length > 0 ? assets : undefined}
        initialIndex={initialIndex}
      />
    </FullscreenViewerContext.Provider>
  );
}

/**
 * The viewer, fenced to the calling component's mount: this provider is a
 * root, above the admitted workspace subtree, and that subtree unmounts when
 * its admission ends. A late `open` (an await that resolves after a
 * workspace switch or membership loss) from an unmounted caller is dropped,
 * so it can't reopen the viewer with the previous workspace's media (#4628).
 */
export function useFullscreenViewer(): FullscreenViewerContextValue {
  const ctx = useContext(FullscreenViewerContext);
  if (!ctx) {
    throw new Error(
      'useFullscreenViewer must be used within a FullscreenViewerProvider'
    );
  }
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const { open: rootOpen } = ctx;
  const open = useCallback(
    (options: OpenOptions) => {
      if (mountedRef.current) rootOpen(options);
    },
    [rootOpen]
  );
  return useMemo(() => ({ ...ctx, open }), [ctx, open]);
}
